import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Badge from '../ui/Badge';
import type { Tone } from '../ui/tone';
import { api } from '../../services/api';
import type { BugReportStatus, BugReportVerdict } from '../../types';
import { formatDateTime } from '../../utils/format';

const STATUS_TONE: Record<BugReportStatus, Tone> = {
  new: 'info',
  reviewed: 'brand',
  fixing: 'warning',
  fixed_local: 'violet',
  approved_cloud: 'brand',
  in_cloud: 'success',
  closed: 'neutral',
  answered: 'success',
  handled: 'success',
  wont_fix: 'neutral',
};

/** D-323 s6: the treatment status the reporter and the OTE manager both see. */
export function BugStatusBadge({ status, verdict }: { status?: BugReportStatus | null; verdict?: BugReportVerdict | null }) {
  const { t } = useTranslation();
  const s = status || 'new';
  return (
    <span className="inline-flex flex-wrap items-center gap-1" data-testid="bug-status">
      <Badge tone={STATUS_TONE[s]} dot>{t(`bugFlow.status.${s}`)}</Badge>
      {verdict && <Badge variant="outline" tone="neutral">{t(`bugFlow.verdict.${verdict}`)}</Badge>}
    </span>
  );
}

export interface BugHistoryItem { status: BugReportStatus; at: string; by: string | null; note?: string; verdict?: string | null; kind?: string }

export function BugHistory({ items }: { items: BugHistoryItem[] }) {
  const { t } = useTranslation();
  if (!items || items.length === 0) return null;
  return (
    <ol className="space-y-1 text-sm text-text-2" aria-label={t('bugFlow.history')}>
      {[...items].reverse().map((h, i) => (
        <li key={`${h.at}-${i}`} className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-medium">
            {h.kind === 'reply' ? t('bugFlow.msgFromManager') : h.kind === 'reporter_reply' ? t('bugFlow.msgFromReporter') : t(`bugFlow.status.${h.status}`)}
          </span>
          {h.verdict && <span>({t(`bugFlow.verdict.${h.verdict}`)})</span>}
          <span className="text-xs text-text-3">{formatDateTime(h.at)}</span>
          {h.note && <span className="w-full text-text-2">{h.note}</span>}
        </li>
      ))}
    </ol>
  );
}

/** An authenticated stored image / voice note, fetched as a blob (an <img src> cannot send the token). */
export function BugMedia({ ticketId, kind, label }: { ticketId: string; kind: 'screenshot' | 'full' | 'audio'; label: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;
    api.getBugReportMedia(ticketId, kind)
      .then((blob) => {
        if (cancelled) return;
        revoked = URL.createObjectURL(blob);
        setUrl(revoked);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (revoked) URL.revokeObjectURL(revoked); };
  }, [ticketId, kind]);
  if (failed) return null;
  if (!url) return <div className="h-24 animate-pulse rounded-lg bg-surface-100" aria-hidden="true" />;
  return (
    <figure className="space-y-1" data-testid={`bug-media-${kind}`}>
      <figcaption className="text-xs text-text-3">{label}</figcaption>
      {kind === 'audio'
        ? <audio src={url} controls className="w-full"><track kind="captions" /></audio>
        : <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={label} className="max-h-72 rounded-lg border border-border" /></a>}
    </figure>
  );
}
